import { createContext, useContext } from 'react';
import type { PropertyTable } from './metadata/propertyConfig';

/**
 * Live add-to-cart state for the property list. Table `component` cells are
 * baked into row data at fetch time, so they can't read the list's live
 * cart/in-flight state directly the way the card grid does via render props.
 * Cells consume this context instead, re-rendering as the cart membership or
 * the in-flight property changes. Provided by PropertyListPage.
 */
export interface PropertyCartContextValue {
  /** Id of the property whose add-to-cart mutation is currently in flight. */
  addingPropertyId: string | null;
  /** Property ids already in the cart — drives the "Added" success state. */
  propertyIdsInCart: Set<string>;
  /** Adds the given row to the cart (centralized single-flight handler). */
  onAddToCart: (row: PropertyTable) => void;
}

const PropertyCartContext = createContext<PropertyCartContextValue | null>(null);

export const PropertyCartProvider = PropertyCartContext.Provider;

export function usePropertyCart(): PropertyCartContextValue {
  const ctx = useContext(PropertyCartContext);
  if (!ctx) {
    throw new Error('usePropertyCart must be used within a PropertyCartProvider');
  }
  return ctx;
}
